import { makeUrl, getDataFromLocation } from '@/utils/sportsbook.js';
import { debug } from '@/utils/debug.js';

type Config = {
  sdk?: any;
  sportsbookInstance?: any;
};

const defaultConfig: Config = {
  sdk: undefined,
  sportsbookInstance: undefined,
};

export class AltenarSportsbook {
  private static instance: AltenarSportsbook;
  private config: Config;
  private internalPath: Ref<AltenarSportsbook.PathEntry | undefined> = ref();
  private queue: AltenarSportsbook.SetMethodPayload[] = [];
  private isInitialized = false;

  public static getInstance(): AltenarSportsbook {
    if (!AltenarSportsbook.instance) {
      AltenarSportsbook.instance = new AltenarSportsbook();
    }

    return AltenarSportsbook.instance;
  }

  constructor(config?: Config) {
    this.config = config || defaultConfig;
  }

  /**
   * Updates instance configs
   * @param config Config
   * @returns void
   */
  public updateConfig(config: Config): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Returns Altenar Instance SDK
   * @returns Config['sdk']
   */
  public get sdkInstance(): Config['sdk'] {
    const { sdk } = this.config;

    return sdk;
  }

  /**
   * Executes queued set method calls.
   * @returns void
   */
  public executeQueue(): void {
    debug('altenar: executing queue', this.queue);

    this.isInitialized = true;

    if (this.queue.length === 0) {
      return;
    }

    for (const payload of this.queue) {
      this.set(payload);
    }

    this.queue = [];
  }

  /**
   * Initialize AltenarWSDK instance.
   * @param payload
   * @returns void
   */
  public init(payload: AltenarSportsbook.InitMethodPayload): void {
    if (!payload) {
      return;
    }

    try {
      if (!this.sdkInstance) {
        if (!window.altenarWSDK) {
          throw new Error('No SDK found on window object');
        }

        this.updateConfig({ sdk: window.altenarWSDK });
      }

      const { container, props, ...rest } = payload;

      this.sdkInstance?.init({ ...rest });

      if (!this.isInitialized) {
        const allowedKeys = [
          'token',
          'culture',
          'numFormat',
          'oddsFormat',
          'themeName',
        ] as const;

        const queueEntry = {} as AltenarSportsbook.SetMethodPayload;

        for (const key of allowedKeys) {
          if (payload[key] !== undefined) {
            (queueEntry as Record<string, unknown>)[key] = payload[key];
          }
        }

        this.queue.push(queueEntry);

        debug('altenar: push to queue', payload);
      }

      const sportsbookInstance = this.sdkInstance?.addSportsBook({
        container,
        props,
      });

      this.updateConfig({ sportsbookInstance });

      if (this.internalPath.value) {
        this.set(this.internalPath.value);
      }
    } catch (err) {
      console.error(`altenar: error on initializing script\n${err}`);
    }
  }

  /**
   * Kill AltenarWSDK instance.
   * @param payload
   * @returns void
   */
  public kill(): void {
    const { sportsbookInstance } = this.config;

    try {
      if (!sportsbookInstance) {
        throw new Error('No sportsbook instance');
      }

      sportsbookInstance?.remove();
    } catch (err) {
      console.error(`altenar: error on kill script\n${err}`);
    }
  }

  /**
   * Set properties into AltenarWSDK instance.
   * @param payload
   * @returns void
   */
  public set(payload: AltenarSportsbook.SetMethodPayload): void {
    if (!payload) {
      return;
    }

    try {
      if (!this.isInitialized) {
        this.queue.push(payload);

        debug('altenar: push to queue', payload);
      }

      if (!this.sdkInstance) {
        throw new Error('No instance available');
      }

      this.sdkInstance?.set(payload);

      debug('altenar: set method called with payload', payload);
    } catch (err) {
      console.error(`altenar: error on set script\n${err}`);
    }
  }

  /**
   * Returns if current page is Altenar Home Page
   * @returns boolean
   */
  public get isSportsbookHomePage(): boolean {
    return this.isSportBookPageActive({ page: 'overview' });
  }

  /**
   * Returns if current page is active
   * @returns boolean
   */
  public isSportBookPageActive(path?: AltenarSportsbook.PathEntry): boolean {
    if (!path) {
      return false;
    }

    const internalPath = this.internalPath.value;

    if (!internalPath?.page && path.page === 'overview') {
      return true;
    }

    if (internalPath?.page === 'championship') {
      const internalId = internalPath.championshipIds?.[0];
      const pathId = path.championshipIds?.[0];

      return internalId === pathId;
    }

    return internalPath?.page === path?.page;
  }

  getReadableUrl(to: AltenarSportsbook.PathEntry, slug?: string): string {
    if (!to) return '';

    const prefix = '/sports';

    switch (to.page) {
      case 'overview':
        return prefix;

      case 'coupon':
        this.goToPage({
          page: 'coupon',
          couponType: to.couponType,
          sportId: to.sportId,
          widget: 'WCoupons',
        });

        return `/sports/coupon-${to.couponType}/s-${to.sportId}`;

      case 'betHistory':
        return `${prefix}/my-bets`;

      case 'live':
        return `${prefix}/live`;

      case 'virtual':
        return `${prefix}/virtual`;

      case 'toto':
        return `${prefix}/toto`;
    }

    const {
      categoryId,
      categoryName,
      sportId,
      sportName,
      eventId,
      eventName,
      championshipId,
      championshipName,
    } = getDataFromLocation(to);

    function makeReadableUrl(suffix: string): string {
      return makeUrl(
        prefix,
        sportName,
        categoryName,
        championshipName,
        eventName,
        suffix,
      );
    }

    switch (to.page) {
      case 'esports': {
        const esportsSuffix = championshipId
          ? `${championshipId}-ch`
          : categoryId
            ? `${categoryId}-ca`
            : sportId
              ? `${sportId}-sp`
              : '';

        if (!esportsSuffix) {
          return `${prefix}/e-sports`;
        }

        return makeReadableUrl(`es-${esportsSuffix}`);
      }

      case 'sport': {
        if (!sportId) return '';

        const sportSuffix = to.sportTypeId
          ? `sp-${sportId}-${to.sportTypeId}`
          : `sp-${sportId}`;

        if (slug) return `${prefix}/${slug}/${sportSuffix}`;

        return makeReadableUrl(sportSuffix);
      }

      case 'category': {
        if (!categoryId) return '';

        if (slug) return `${prefix}/${slug}/ca-${categoryId}`;

        return makeReadableUrl(`ca-${categoryId}`);
      }

      case 'championship': {
        if (!championshipId) return '';

        if (slug) return `${prefix}/${slug}/ch-${championshipId}`;

        return makeReadableUrl(`ch-${championshipId}`);
      }

      case 'event': {
        if (!eventId) return '';

        if (slug) return `${prefix}/${slug}/ev-${eventId}`;

        return makeReadableUrl(`ev-${eventId}`);
      }

      case 'liveEvent': {
        if (!eventId) return '';

        if (slug) return `${prefix}/${slug}/le-${eventId}`;

        return makeReadableUrl(`le-${eventId}`);
      }

      case 'coupon': {
        if (!sportId || !to.couponType) return '';

        return makeReadableUrl(`co-${sportId}-${to.couponType}`);
      }

      default:
        debug('altenar: getReadableUrl ~ default case');

        return '';
    }
  }

  /**
   * Go to page via Path Entry.
   * @param pathEntry
   * @param callback
   * @returns void
   */
  goToPage(path: AltenarSportsbook.PathEntry): void {
    if (!path) {
      return;
    }

    this.set(path);

    this.internalPath.value = path;
  }
}
